import endpointsConfig from './wp-cache-endpoints.json';
import { URL_Back } from '../components/Pages/Variables';

const { endpoints } = endpointsConfig;

function getEndpoint(id) {
  const ep = endpoints.find((e) => e.id === id);
  if (!ep) {
    throw new Error(`wpClient: unknown cache endpoint "${id}"`);
  }
  return ep;
}

function localCacheUrl(file) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/wp-cache/${file}`;
}

async function fetchPaginatedRemote(paginatedBase) {
  let all = [];
  let page = 1;
  let totalPages = 1;
  do {
    const sep = paginatedBase.includes('?') ? '&' : '?';
    const url = `${URL_Back}/wp-json/${paginatedBase}${sep}page=${page}`;
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`wpClient: ${url} → ${res.status}`);
    }
    const chunk = await res.json();
    if (!Array.isArray(chunk)) {
      break;
    }
    all = all.concat(chunk);
    totalPages = parseInt(res.headers.get('X-WP-TotalPages') || String(page), 10);
    page += 1;
  } while (page <= totalPages);
  return all;
}

/**
 * Stale-while-revalidate: serve /public/wp-cache/*.json immediately when present,
 * then refresh from WordPress in the background (onRevalidated).
 */
export async function fetchWpCached(endpointId, options = {}) {
  const { onRevalidated } = options;
  const ep = getEndpoint(endpointId);
  const storageKey = `wp-cache-${endpointId}`;

  const runRemote = async () => {
    if (ep.paginatedBase) {
      return fetchPaginatedRemote(ep.paginatedBase);
    }

    const url = `${URL_Back}/wp-json/${ep.path}`;
    const res = await fetch(url, { cache: "no-store" });

    if (!res.ok) {
      throw new Error(`wpClient: ${url} → ${res.status}`);
    }

    return res.json();
  };

  const notify = (data) => {
    if (data != null && typeof onRevalidated === "function") {
      onRevalidated(data);
    }
  };

  let localData = null;

  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      localData = JSON.parse(stored);
    }
  } catch {
    /* ignore localStorage errors */
  }

  const remotePromise = runRemote()
    .then((data) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(data));
      } catch {
        /* ignore localStorage errors */
      }

      notify(data);
      return data;
    })
    .catch(() => null);

  if (localData != null) {
    void remotePromise;
    return localData;
  }

  const remoteData = await remotePromise;

  if (remoteData != null) {
    return remoteData;
  }

  throw new Error(`wpClient: no cache and remote failed for "${endpointId}"`);
}

/**
 * Article par slug : liste complète en cache d’abord, puis revalidation REST (?slug=).
 */
export async function fetchPostBySlug(slug, options = {}) {
  const { onRevalidated } = options;
  if (!slug) return null;

  let fromList = null;
  try {
    const all = await fetchWpCached("posts");
    if (Array.isArray(all)) {
      fromList = all.find((p) => p.slug === slug) || null;
    }
  } catch {
    /* ignore */
  }

  const remotePromise = fetch(
    `${URL_Back}/wp-json/wp/v2/posts?slug=${encodeURIComponent(slug)}`
  )
    .then((r) => (r.ok ? r.json() : []))
    .then((arr) => (Array.isArray(arr) && arr[0] ? arr[0] : null))
    .then((post) => {
      if (post && typeof onRevalidated === "function") {
        onRevalidated(post);
      }
      return post;
    })
    .catch(() => null);

  if (fromList) {
    void remotePromise;
    return fromList;
  }

  return await remotePromise;
}

/**
 * Article par id (liens internes dans le HTML) : cache posts puis REST.
 */
export async function fetchWpPostById(id) {
  if (id == null || id === "") return null;
  try {
    const all = await fetchWpCached("posts");
    if (Array.isArray(all)) {
      const hit = all.find((p) => String(p.id) === String(id));
      if (hit?.slug) return hit;
    }
  } catch {
    /* ignore */
  }
  try {
    const res = await fetch(`${URL_Back}/wp-json/wp/v2/posts/${id}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export function formatWpImageUrl(url, options = {}) {
  const { preserveThumbnail = false } = options;

  if (!url || typeof url !== "string") {
    return "";
  }

  if (!/\/wp-content\/uploads\//i.test(url)) {
    return url;
  }

  if (preserveThumbnail) {
    return url;
  }

  return url.replace(
    /-150x150(\.(?:avif|gif|jpe?g|png|svg|webp))(\?.*)?$/i,
    "$1$2"
  );
}

export function resolvePostImageUrl(post, imageField, options = {}) {
  const imageUrl = imageField ? post?.[imageField]?.[0]?.src : "";
  return formatWpImageUrl(imageUrl, options);
}

export function filterPortfolioByLanguage(allPortfolios, currentLanguage) {
  if (!Array.isArray(allPortfolios)) {
    return [];
  }
  return allPortfolios.filter((element) => {
    if (currentLanguage === 'fr') {
      return element.excerpt.rendered === '<p>French</p>\n';
    }
    if (currentLanguage === 'en') {
      return element.excerpt.rendered === '<p>English</p>\n';
    }
    if (currentLanguage === 'ar') {
      return element.excerpt.rendered === '<p>العربية</p>\n';
    }
    return false;
  });
}
